import { memo, useState, useEffect, useRef, useCallback } from 'react';
import { Star, Quotes as Quote, ChatCircleText as MessageSquarePlus, X, SealCheck, BuildingOffice } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCountry } from '@/hooks/useCountry';
import { useTestimonials } from '@/hooks/useTestimonials';
import { useToast } from '@/hooks/use-toast';
import { useSearchParams } from '@/lib/router-compat';

interface Testimonial {
  name: string;
  role: string;
  rating: number;
  text: string;
  location: string;
  company?: string;
  badge?: string;
}

const TestimonialsSectionComponent = () => {
  const { country } = useCountry();

  const getTestimonials = (): Testimonial[] => {
    if (country === 'UK') {
      return [
        {
          name: 'James Anderson',
          role: 'Dealer Principal',
          rating: 5,
          text: 'The platform gives us a clean way to publish verified stock and keeps buyer enquiries better qualified than generic marketplaces.',
          location: 'London, UK',
          company: 'Anderson Motor Group',
          badge: 'Verified dealer',
        },
        {
          name: 'Sarah Williams',
          role: 'Used Car Buyer',
          rating: 5,
          text: 'Dealer information was clear, the listing quality felt professional, and the verification layer gave me confidence before contacting the seller.',
          location: 'Manchester, UK',
          badge: 'Verified review',
        },
        {
          name: 'Michael Brown',
          role: 'Fleet Manager',
          rating: 5,
          text: 'For fleet sourcing, the stock quality and response speed are strong. It is easy to compare dealer inventory in one place.',
          location: 'Birmingham, UK',
          badge: 'Verified review',
        },
      ];
    }

    if (country === 'DE') {
      return [
        {
          name: 'Klaus Schmidt',
          role: 'Autohaus Inhaber',
          rating: 5,
          text: 'Die Plattform wirkt professionell, die Inserate sind hochwertig dargestellt und die Händler-Präsentation schafft Vertrauen beim ersten Besuch.',
          location: 'Berlin, Deutschland',
          company: 'Schmidt Autohaus',
          badge: 'Geprüfter Händler',
        },
        {
          name: 'Anna Müller',
          role: 'Privatkäuferin',
          rating: 5,
          text: 'Ich konnte schnell seriöse Angebote filtern und hatte durch die verifizierten Händler deutlich mehr Sicherheit beim Kaufprozess.',
          location: 'München, Deutschland',
          badge: 'Verifizierte Bewertung',
        },
        {
          name: 'Stefan Weber',
          role: 'Flottenmanager',
          rating: 5,
          text: 'Für gewerbliche Fahrzeugsuche ist die Übersicht stark. Wir sparen Zeit, weil relevante Händler und Bestände schneller sichtbar sind.',
          location: 'Frankfurt, Deutschland',
          badge: 'Verifizierte Bewertung',
        },
      ];
    }

    // Romanian testimonials
    return [
      {
        name: 'Daniel Ionescu',
        role: 'Local Guide',
        rating: 5,
        text: 'A acordat RO Auto Market o recenzie Google de 5 stele.',
        location: 'România',
        company: 'Google Reviews • 8 recenzii • 122 fotografii',
        badge: 'Recenzie Google',
      },
      {
        name: 'Ilona Rasina',
        role: 'Dealer auto',
        rating: 5,
        text: 'RO Auto Market este o platformă serioasă de anunțuri auto din România. Am văzut cum funcționează și sunt impresionată de cât de ușor poți lista vehicule și urmări statisticile în timp real.',
        location: 'România',
      },
      {
        name: 'FD VOIAJ IMPEX SRL Ficut',
        role: 'Dealer auto',
        rating: 5,
        text: 'Sunt mulțumit de serviciile RO Auto Market și recomand platforma pentru prezentarea stocului într-un mod clar și profesionist.',
        location: 'România',
        company: 'Dealer verificat',
        badge: 'Recenzie verificată',
      },
      {
        name: 'Andrei Popa',
        role: 'Dealer auto',
        rating: 5,
        text: 'Ca dealer, sunt foarte mulțumit de calitatea lead-urilor. Clienții sunt serioși, bine informați, iar platforma inspiră încredere din prima interacțiune.',
        location: 'București, România',
        company: undefined,
      },
      {
        name: 'Maria Ionescu',
        role: 'Vânzător',
        rating: 5,
        text: 'Am vândut mașina în mai puțin de o săptămână. Interfața este intuitivă și am primit întrebări relevante de la cumpărători serioși.',
        location: 'Cluj-Napoca, România',
      },
      {
        name: 'Cristina Marinescu',
        role: 'Cumpărător',
        rating: 5,
        text: 'Am cumpărat un Audi A4 în condiții excelente. Dealerul verificat a fost foarte corect, iar informațiile din anunț au fost clare și complete.',
        location: 'Constanța, România',
      },
    ];
  };

  const testimonials = getTestimonials();

  const getTitle = () => {
    if (country === 'UK') return 'What Our Customers Say';
    if (country === 'DE') return 'Was Unsere Kunden Sagen';
    return 'Ce spun clienții';
  };

  const [showForm, setShowForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rating, setRating] = useState(5);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    city: '',
    user_type: 'buyer' as 'buyer' | 'seller' | 'dealer',
    message: ''
  });

  const nameInputRef = useRef<HTMLInputElement>(null);
  const modalCardRef = useRef<HTMLDivElement>(null);
  
  const { submitTestimonial, isSubmitting } = useTestimonials();
  const { toast } = useToast();

  const openForm = useCallback(() => {
    setShowForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
    // Clean the URL param so a refresh doesn't reopen the modal unexpectedly.
    if (searchParams.get('openForm') === 'true') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openForm');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Open form if URL has ?openForm=true or if another component requests it.
  useEffect(() => {
    const fromParams = searchParams.get('openForm') === 'true';
    const fromWindow = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('openForm') === 'true';
    if (fromParams || fromWindow) {
      setShowForm(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleOpen = () => openForm();
    window.addEventListener('open-testimonial-form', handleOpen);
    return () => window.removeEventListener('open-testimonial-form', handleOpen);
  }, [openForm]);

  // When the modal opens, bring it into view and focus the first field.
  useEffect(() => {
    if (!showForm) return;
    const timer = setTimeout(() => {
      modalCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [showForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.city || !formData.message) {
      toast({
        title: country === 'UK' ? 'Error' : country === 'DE' ? 'Fehler' : 'Eroare',
        description: country === 'UK' 
          ? 'Please fill in all fields' 
          : country === 'DE' 
          ? 'Bitte füllen Sie alle Felder aus' 
          : 'Te rog completează toate câmpurile',
        variant: 'destructive'
      });
      return;
    }

    const success = await submitTestimonial({
      name: formData.name,
      email: formData.email,
      city: formData.city,
      user_type: formData.user_type,
      rating,
      message: formData.message
    });

    if (success) {
      setFormData({ name: '', email: '', city: '', user_type: 'buyer', message: '' });
      setRating(5);
      closeForm();
      toast({
        title: country === 'UK' ? 'Thank you!' : country === 'DE' ? 'Danke!' : 'Mulțumim!',
        description: country === 'UK' 
          ? 'Your testimonial has been submitted for review.' 
          : country === 'DE' 
          ? 'Ihre Bewertung wurde zur Überprüfung eingereicht.' 
          : 'Testimonialul tău a fost trimis pentru verificare.',
      });
    }
  };

  const getFormLabels = () => {
    if (country === 'UK') {
      return {
        title: 'Leave a Testimonial',
        subtitle: 'Share your experience with RO Auto Market',
        name: 'Full Name',
        email: 'Email',
        city: 'City',
        userType: 'I am a...',
        buyer: 'Buyer',
        seller: 'Seller',
        dealer: 'Dealer',
        rating: 'Rating',
        message: 'Your Message',
        messagePlaceholder: 'Tell us about your experience...',
        submit: 'Submit Testimonial',
        cancel: 'Cancel'
      };
    }
    if (country === 'DE') {
      return {
        title: 'Bewertung abgeben',
        subtitle: 'Teilen Sie Ihre Erfahrung mit RO Auto Market',
        name: 'Vollständiger Name',
        email: 'E-Mail',
        city: 'Stadt',
        userType: 'Ich bin...',
        buyer: 'Käufer',
        seller: 'Verkäufer',
        dealer: 'Händler',
        rating: 'Bewertung',
        message: 'Ihre Nachricht',
        messagePlaceholder: 'Erzählen Sie uns von Ihrer Erfahrung...',
        submit: 'Bewertung absenden',
        cancel: 'Abbrechen'
      };
    }
    return {
      title: 'Lasă un testimonial',
      subtitle: 'Spune-ne despre experiența ta cu RO Auto Market',
      name: 'Nume Complet',
      email: 'Email',
      city: 'Oraș',
      userType: 'Sunt...',
      buyer: 'Cumpărător',
      seller: 'Vânzător',
      dealer: 'Dealer',
      rating: 'Rating',
      message: 'Mesajul Tău',
      messagePlaceholder: 'Spune-ne despre experiența ta...',
      submit: 'Trimite Testimonial',
      cancel: 'Anulează'
    };
  };

  const labels = getFormLabels();

  return (
    <section id="testimoniale" className="w-full py-12 px-4 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{getTitle()}</h2>
          <p className="text-muted-foreground text-lg mb-6">
            {country === 'UK'
              ? 'Trusted by buyers and sellers'
              : country === 'DE'
              ? 'Vertraut von Tausenden von Käufern und Verkäufern'
              : 'De încredere pentru cumpărători și vânzători'}
          </p>
          
          {/* CTA Button */}
          <Button 
            onClick={() => setShowForm(true)} 
            size="lg"
            className="gap-2"
          >
            <MessageSquarePlus className="w-5 h-5" />
            {country === 'UK' ? 'Leave a Testimonial' : country === 'DE' ? 'Bewertung abgeben' : 'Lasă un testimonial'}
          </Button>
        </div>

        {/* Testimonial Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card ref={modalCardRef} className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{labels.title}</h3>
                    <p className="text-sm text-muted-foreground">{labels.subtitle}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={closeForm}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">{labels.name} *</Label>
                    <Input
                      ref={nameInputRef}
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ion Popescu"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">{labels.email} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="ion@email.ro"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="city">{labels.city} *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="București"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>{labels.userType} *</Label>
                    <Select 
                      value={formData.user_type} 
                      onValueChange={(value: 'buyer' | 'seller' | 'dealer') => 
                        setFormData(prev => ({ ...prev, user_type: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buyer">{labels.buyer}</SelectItem>
                        <SelectItem value="seller">{labels.seller}</SelectItem>
                        <SelectItem value="dealer">{labels.dealer}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>{labels.rating} *</Label>
                    <div className="flex gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoveredRating(star)}
                          onMouseLeave={() => setHoveredRating(0)}
                          className="p-1 transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-8 h-8 ${
                              star <= (hoveredRating || rating)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-muted-foreground'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">{labels.message} *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder={labels.messagePlaceholder}
                      rows={4}
                      required
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={closeForm}
                      className="flex-1"
                    >
                      {labels.cancel}
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? '...' : labels.submit}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <Card
              key={index}
              className="border border-border bg-card shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover hover:border-brand-gold/40"
            >
              <CardContent className="p-6">
                {/* Quote Icon */}
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-brand-gold/30 bg-brand-gold/10 text-brand-gold">
                    <BuildingOffice className="h-6 w-6" />
                  </div>
                </div>

                <Quote className="h-8 w-8 text-primary/30 mb-4" />

                {/* Rating Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-foreground mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>

                {/* Author Info */}
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/5">
                    <span className="text-sm font-black uppercase text-primary">{testimonial.name.split(' ').map(part => part[0]).join('').slice(0, 2)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {testimonial.role}
                    </p>
                    {testimonial.company && (
                      <p className="text-xs font-medium text-primary mt-1">
                        {testimonial.company}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export const TestimonialsSection = memo(TestimonialsSectionComponent);
